import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useKid } from "@/context/KidContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Field } from "@/pages/Login";

const STEPS = ["intro", "child", "interests", "passport"];

export default function Onboarding() {
  const { user } = useAuth();
  const { kids, reloadKids, taxonomy } = useKid();
  const navigate = useNavigate();
  const [step, setStep] = useState("intro");
  const [child, setChild] = useState({ name: "", birth_date: "", avatar: "🧒" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/onboarding/state").then(({ data }) => {
      const s = data?.onboarding || {};
      if (s.completed) { navigate("/app"); return; }
      if (s.current_step && STEPS.includes(s.current_step)) setStep(s.current_step);
    }).catch(() => {});
  }, [navigate]);

  const save = async (patch) => {
    try { await api.post("/onboarding/update", patch); } catch {}
  };

  const next = async (target) => {
    await save({ current_step: target });
    setStep(target);
  };

  const finish = async () => {
    await api.post("/onboarding/complete");
    toast.success("Vamos começar 💛");
    navigate("/app");
  };

  const createChild = async (e) => {
    e.preventDefault();
    setErr("");
    if (!child.name.trim()) return setErr("Nome é obrigatório");
    if (!child.birth_date) return setErr("Data é obrigatória");
    setSaving(true);
    try {
      await api.post("/children", { name: child.name.trim(), birth_date: child.birth_date, avatar: child.avatar });
      await reloadKids();
      await save({ child_created: true });
      await next("passport");
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-10" data-testid="onboarding-page">
      <div className="w-full max-w-lg card-soft p-8">
        <div className="flex items-center gap-2 text-xs font-bold text-[#243B53]/60">
          {STEPS.map((s, i) => (
            <div key={s} className={`flex-1 h-1.5 rounded-full ${STEPS.indexOf(step) >= i ? "bg-[#FFD54F]" : "bg-[#243B53]/10"}`} />
          ))}
        </div>

        {step === "intro" && (
          <div className="mt-6 text-center" data-testid="onboarding-intro">
            <div className="text-5xl">💛</div>
            <h1 className="mt-3 text-2xl font-extrabold text-[#243B53]">Bem-vindo(a) ao Clubinho da Fé!</h1>
            <p className="mt-3 text-[#243B53]/70">Vamos configurar rapidamente para que a experiência seja perfeita para sua família.</p>
            <div className="mt-6 grid grid-cols-3 gap-3 text-center text-sm">
              <div className="card-soft p-4"><div className="text-2xl">📖</div><div className="mt-1 font-bold">Histórias</div></div>
              <div className="card-soft p-4"><div className="text-2xl">🎯</div><div className="mt-1 font-bold">Missões</div></div>
              <div className="card-soft p-4"><div className="text-2xl">🛂</div><div className="mt-1 font-bold">Passaporte</div></div>
            </div>
            <button data-testid="onboarding-next-intro" className="btn-primary mt-8 w-full" onClick={() => next(kids.length ? "passport" : "child")}>
              Vamos começar
            </button>
            <button data-testid="onboarding-skip" onClick={finish} className="mt-3 text-sm text-[#243B53]/60 underline">
              Pular por enquanto
            </button>
          </div>
        )}

        {step === "child" && (
          <form onSubmit={createChild} className="mt-6 space-y-4" data-testid="onboarding-child">
            <h1 className="text-2xl font-extrabold text-[#243B53]">Cadastre sua primeira criança</h1>
            <p className="text-sm text-[#243B53]/60">Não pedimos endereço, escola ou telefone da criança.</p>
            <Field label="Nome ou apelido" value={child.name} onChange={(v) => setChild({ ...child, name: v })} testid="onboarding-child-name" />
            <Field label="Data de nascimento" type="date" value={child.birth_date} onChange={(v) => setChild({ ...child, birth_date: v })} testid="onboarding-child-birth" />
            <div>
              <div className="text-xs font-bold text-[#243B53]/70 mb-2">Avatar</div>
              <div className="flex flex-wrap gap-2">
                {["🧒","👦","👧","👶","🐣","🌟"].map(a => (
                  <button key={a} type="button" onClick={() => setChild({...child, avatar: a})}
                          className={`w-11 h-11 rounded-full text-2xl border-2 ${child.avatar===a?"border-[#FFD54F] bg-[#FFF9ED]":"border-transparent"}`}>{a}</button>
                ))}
              </div>
            </div>
            {err && <div data-testid="onboarding-error" className="text-red-600 text-sm font-bold">{err}</div>}
            <button data-testid="onboarding-child-submit" disabled={saving} className="btn-primary w-full">
              {saving ? "Salvando..." : "Criar perfil"}
            </button>
            <button type="button" onClick={() => next("passport")} className="w-full text-sm text-[#243B53]/60 underline">Fazer isso depois</button>
          </form>
        )}

        {step === "passport" && (
          <div className="mt-6 text-center" data-testid="onboarding-passport">
            <div className="text-5xl">🛂</div>
            <h1 className="mt-3 text-2xl font-extrabold text-[#243B53]">Passaporte da Fé</h1>
            <p className="mt-3 text-[#243B53]/70">A cada história, atividade e oração, a criança acumula estrelas e pontos, evolui de nível e desbloqueia conquistas.</p>
            <div className="mt-6 grid grid-cols-3 gap-3 text-center">
              <div className="card-soft p-3"><div className="text-2xl">⭐</div><div className="mt-1 text-xs font-bold">Estrelas</div></div>
              <div className="card-soft p-3"><div className="text-2xl">✨</div><div className="mt-1 text-xs font-bold">Pontos</div></div>
              <div className="card-soft p-3"><div className="text-2xl">🔥</div><div className="mt-1 text-xs font-bold">Sequência</div></div>
            </div>
            <button data-testid="onboarding-finish" className="btn-primary mt-8 w-full" onClick={finish}>Explorar o Clubinho</button>
          </div>
        )}
      </div>
    </div>
  );
}
